import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { FOCUS_LEAD_PARAM } from '@/shared/routes'
import type { FocusTab } from '@/features/leads/focus/FocusView'
import type { LeadRow } from '@/shared/types'
import type { useLeadsData } from './useLeadsData'
import { clearStored, readStored, writeStored } from '@/shared/lib/storage'
import { trackEvent } from '@/shared/api/telemetry'
import { triggerMeta, type KeyTrigger } from '@/shared/api/trackMeta'
import { holdsPendingJump } from './focusLoad'

const SELECTED_ID_KEY = 'focus.selectedLeadId'

function loadSelectedId(): string | null {
  return readStored(SELECTED_ID_KEY, { legacyKey: 'crm.selectedLeadId' }) || null
}

interface LeadSelectionArgs {
  list: LeadRow[]
  leadsData: ReturnType<typeof useLeadsData>['leadsData']
  leads: LeadRow[]
  /** Mirror the selection into ?current=. Focus only: elsewhere the selection
   *  is not on screen, so the URL should not claim it. */
  inUrl: boolean
  /** Selected when its id is, though `leads` lacks it. */
  fallback?: LeadRow | null
}

interface LeadSelection {
  selectedId: string | null
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>
  selected: LeadRow | null
  idx: number
  nav: 'forward' | 'back' | null
  tab: FocusTab
  setTab: React.Dispatch<React.SetStateAction<FocusTab>>
  advance: () => void
  navigate: (dir: 'forward' | 'back', via?: KeyTrigger) => void
  jumpToLead: (leadId: string) => void
  resetToFirst: (via?: KeyTrigger) => void
}

export function useLeadSelection({ list, leadsData, leads, inUrl, fallback }: LeadSelectionArgs): LeadSelection {
  const [params, setParams] = useSearchParams()
  // The URL wins over storage: it is what a reload or a shared link carries.
  // An id that is not in the queue falls through to the validate effect below
  // like any stale selection, which lands on the first lead.
  const [selectedId, setSelectedId] = useState<string | null>(
    () => params.get(FOCUS_LEAD_PARAM) || loadSelectedId(),
  )
  // Set while a jump to a specific lead is in flight, so the validate effect
  // below leaves it alone instead of substituting the head of the list.
  const pendingJumpRef = useRef<string | null>(null)
  const [tab, setTab] = useState<FocusTab>('handover')
  // Navigation (Skip / Back) animation: 'forward' = current recedes, next
  // emerges; 'back' = current recedes (other way), previous emerges. Distinct
  // from outcome `swipe` which still drives the directional fly-off per
  // outcome (Booked flies up, Not a fit slides left, etc.).
  const [nav, setNav] = useState<'forward' | 'back' | null>(null)

  // Persist selectedId, but skip writes triggered by the auto-fallback below.
  // Otherwise: saved id "X" → API returns a list without X → validate sets to
  // first → this effect overwrites localStorage with the new id → next
  // reload starts on first forever. We only want USER actions (advance,
  // prev, click) to persist.
  const skipNextSelectedPersist = useRef(false)
  useEffect(() => {
    if (skipNextSelectedPersist.current) {
      skipNextSelectedPersist.current = false
      return
    }
    try {
      if (selectedId) writeStored(SELECTED_ID_KEY, selectedId)
      else clearStored(SELECTED_ID_KEY)
    } catch { /* ignore */ }
  }, [selectedId])

  // Reset selectedId when the saved lead isn't in the current list.
  //
  // The first guard used to exist because `leads` was a useState mirror that
  // lagged the query by a render, so this effect could see an empty list while
  // the server had just returned rows. The list is derived synchronously now,
  // so the guard means what it says instead: the server returned leads but the
  // focus filters hid all of them, and a selection should survive that rather
  // than being cleared by a filter the seller can switch off again.
  //
  // skipNextSelectedPersist keeps the BDR's saved id in localStorage when
  // the auto-fallback fires, so a transient absence (e.g. mid-refetch)
  // doesn't permanently wipe their position.
  useEffect(() => {
    if (!leadsData) return
    if (leadsData.leads.length > 0 && list.length === 0) return
    if (list.length === 0) {
      if (selectedId !== null) {
        skipNextSelectedPersist.current = true
        setSelectedId(null)
      }
      return
    }
    if (pendingJumpRef.current && list.find(l => l.id === pendingJumpRef.current)) {
      pendingJumpRef.current = null
    }
    if (!list.find(l => l.id === selectedId)) {
      // A pending jump is honoured even if the lead is not in the current list.
      // Silently retargeting to list[0] is what made "Jump to lead" on a callback
      // toast land on a stranger's card: a callback is future-dated by definition,
      // so it was never in the filtered list the check runs against.
      // Only while a jump is actually pending. Guarding on equality alone matched
      // null === null on first load, so the initial selection was never seeded and
      // Focus rendered its empty state with a full queue behind it. And only while
      // the lead is in the loaded book: a jump to a lead the response lacks held
      // `selected` at null, and the done screen covered a full queue until reload.
      if (holdsPendingJump(pendingJumpRef.current, selectedId, leads)) return
      pendingJumpRef.current = null
      skipNextSelectedPersist.current = true
      setSelectedId(list[0].id)
    }
  }, [list, selectedId, leadsData, leads])

  // The URL says what is on screen, the fallback included, so a stale id is
  // replaced rather than kept. Re-checked on every URL change, so a write to
  // the search string from elsewhere in the same tick cannot leave it stale.
  const urlLead = params.get(FOCUS_LEAD_PARAM)
  useEffect(() => {
    if (!inUrl || !leadsData || urlLead === selectedId) return
    setParams(prev => {
      const next = new URLSearchParams(prev)
      if (selectedId) next.set(FOCUS_LEAD_PARAM, selectedId)
      else next.delete(FOCUS_LEAD_PARAM)
      return next
    }, { replace: true })
  }, [inUrl, leadsData, urlLead, selectedId, setParams])

  const selected = leads.find(l => l.id === selectedId)
    || (fallback && fallback.id === selectedId ? fallback : null)

  const advance = () => {
    setNav('forward')
    const i = list.findIndex(l => l.id === selectedId)
    const next = list[i + 1]
    setSelectedId(next ? next.id : null)
    setTab('handover')
  }

  // `nav` is the direction the deck should play, read by the card's exit and
  // enter variants. The selection changes immediately: AnimatePresence holds the
  // outgoing card through its animation, which is what the old timer was for.
  //
  // `queue_nav` is reported here rather than on the buttons that used to report
  // it: j/k and the arrow keys reach the queue through this function too, and
  // those are how a fast BDR actually moves, so the button-only instrumentation
  // undercounted exactly the people it was meant to describe.
  const navigate = (dir: 'forward' | 'back', via?: KeyTrigger) => {
    trackEvent('queue_nav', { dir: dir === 'forward' ? 'next' : 'prev', ...triggerMeta(via) })
    setNav(dir)
    if (dir === 'forward') {
      advance()
      return
    }
    const i = list.findIndex(l => l.id === selectedId)
    const p = list[i - 1] || list[list.length - 1]
    if (p) setSelectedId(p.id)
  }

  // Jump to an arbitrary lead in the focus queue. Leaves `nav` as it stands so
  // a jump plays the deck in whichever direction was last travelled rather than
  // asserting a direction that a non-adjacent jump does not really have.
  // Resets the tab so the BDR always lands on the handover view.
  const jumpToLead = (leadId: string) => {
    if (!leadId || leadId === selectedId) return
    pendingJumpRef.current = leadId
    setSelectedId(leadId)
    setTab('handover')
  }
  const resetToFirst = (via?: KeyTrigger) => {
    const first = list[0]
    if (!first) return
    trackEvent('queue_nav', { dir: 'first', ...triggerMeta(via) })
    jumpToLead(first.id)
  }

  const idx = list.findIndex(l => l.id === selectedId)

  return {
    selectedId,
    setSelectedId,
    selected,
    idx,
    nav,
    tab,
    setTab,
    advance,
    navigate,
    jumpToLead,
    resetToFirst,
  }
}
